/**
 * TaskArtifactList — TASK-735 + TASK-743.
 *
 * Version-sensitive evidence pointers grouped UNDER their binding (durable attachment
 * policy: role, requirement, withdrawal). Resolution still goes through the same
 * `resolveArtifact` the review gate uses, so a pointer opens (or fails to open) the same
 * way on every surface.
 *
 * The 735 honesty rule holds everywhere: an audit row is never hidden. A dangling pointer
 * renders with the resolver's own reason; evidence whose identity has no binding in the
 * payload (defensive — the FK should make it impossible) renders under "Ungrouped evidence".
 *
 * Mutations (classify / withdraw / reactivate) need a `taskId`; a mount without one is
 * read-only. `onMutated` lets the owner refetch — this component holds no server state.
 */
import { useState } from 'react';
import { ExternalLink, FileWarning } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import type {
  TaskArtifact,
  TaskArtifactBinding,
  TaskArtifactRole,
  TaskBindingMutationRequest,
} from '../../types/tasks';
import { taskService } from '../../services/taskService';
import { artifactLabel, resolveArtifact } from './taskArtifactResolver';
// TASK-742: the shared card core — one identity rendering on every artifact surface.
import { ArtifactIdentity, formatMeta } from '../canvas/widgets/ArtifactCard';
// TASK-785: one builder for the `?task=` scope, shared with every workbench opener.
import { withTaskScope } from '../../utils/workbenchRoute';

const ROLE_BADGE: Record<TaskArtifactRole, string> = {
  output: 'bg-green-100 text-green-800',
  supporting: 'bg-blue-100 text-blue-700',
  source: 'bg-gray-100 text-gray-600',
  unclassified: 'bg-amber-100 text-amber-800',
};

const identityKey = (x: { artifact_type: string; artifact_id: string }) =>
  `${x.artifact_type}:${x.artifact_id}`;

/**
 * One evidence pointer. Under a binding group the header carries the type label
 * (`showLabel` off); an orphan row carries its own.
 */
function EvidenceRow({
  artifact,
  muted,
  showLabel,
  taskId,
}: {
  artifact: TaskArtifact;
  muted?: boolean;
  showLabel?: boolean;
  taskId?: string;
}) {
  const target = resolveArtifact(artifact);
  // TASK-743: opening a workbench document from a task carries the task scope
  // (?task=) so the shell shows the objective and the agent gets grounding.
  const openTo =
    target.kind === 'route' && taskId && target.to.startsWith('/workbench/')
      ? withTaskScope(target.to, taskId)
      : target.kind === 'route'
        ? target.to
        : undefined;
  return (
    <li
      data-testid="task-artifact-row"
      className={`text-sm ${muted ? 'opacity-60' : ''}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {showLabel && <span className="font-medium text-gray-900">{target.label}</span>}
        {typeof artifact.artifact_version === 'number' && (
          <span className="rounded bg-gray-100 px-1 text-[10px] font-medium text-gray-600">
            v{artifact.artifact_version}
          </span>
        )}
        {target.kind === 'route' && openTo && (
          <Link
            to={openTo}
            aria-label={`Open ${target.label}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-kielis-navy hover:underline"
          >
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            Open
          </Link>
        )}
      </div>
      {target.kind !== 'route' && (
        // Never a dead link: the pointer stays on screen with the reason it cannot be opened.
        <p
          data-testid="task-artifact-note"
          className="mt-0.5 flex items-start gap-1 text-xs text-amber-800"
        >
          <FileWarning className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
          <span>{target.reason}</span>
        </p>
      )}
    </li>
  );
}

/** Inline classify affordance for a legacy `unclassified` binding. */
function ClassifyControl({
  binding,
  busy,
  onClassify,
}: {
  binding: TaskArtifactBinding;
  busy: boolean;
  onClassify: (role: TaskArtifactRole, required: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<TaskArtifactRole>('output');
  const [required, setRequired] = useState(false);
  const roleSelectId = `classify-role-${binding.id}`;
  const requiredId = `classify-required-${binding.id}`;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-200"
      >
        Unclassified — classify
      </button>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
      <label htmlFor={roleSelectId} className="text-gray-600">
        Role
      </label>
      <select
        id={roleSelectId}
        value={role}
        onChange={(e) => setRole(e.target.value as TaskArtifactRole)}
        className="rounded border border-gray-300 px-1 py-0.5 text-xs"
      >
        <option value="output">output</option>
        <option value="supporting">supporting</option>
        <option value="source">source</option>
      </select>
      {/* required_for_completion is a document-only concept (DB CHECK). */}
      {binding.artifact_type === 'document' && (
        <label htmlFor={requiredId} className="inline-flex items-center gap-1 text-gray-600">
          <input
            id={requiredId}
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          Required
        </label>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => onClassify(role, required)}
        className="rounded bg-kielis-navy px-1.5 py-0.5 text-[10px] font-medium text-white disabled:opacity-50"
      >
        Confirm
      </button>
    </span>
  );
}

function BindingGroup({
  binding,
  rows,
  taskId,
  onMutated,
}: {
  binding: TaskArtifactBinding;
  rows: TaskArtifact[];
  taskId?: string;
  onMutated?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [reason, setReason] = useState('');
  const reasonId = `withdraw-reason-${binding.id}`;
  const withdrawn = !binding.is_active;
  const label = artifactLabel(binding);

  const mutate = async (
    body: Omit<TaskBindingMutationRequest, 'artifact_type' | 'artifact_id'>,
  ) => {
    if (!taskId) return;
    setBusy(true);
    try {
      await taskService.mutateTaskBinding(taskId, {
        artifact_type: binding.artifact_type,
        artifact_id: binding.artifact_id,
        ...body,
      });
      setWithdrawOpen(false);
      setReason('');
      onMutated?.();
    } catch {
      // Nondisclosing: missing vs cross-account vs transient stay server-side.
      toast.error('The change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid="task-binding-group" className="text-sm">
      {/* TASK-742: the shared ArtifactIdentity core (same as the canvas/rail
          cards); the binding controls below stay purpose-built. */}
      <ArtifactIdentity
        Icon={formatMeta(null).Icon}
        title={label}
        titleClassName={
          withdrawn
            ? 'truncate text-sm font-medium text-gray-400 line-through'
            : 'truncate text-sm font-medium text-gray-900'
        }
      >
      <div className="mt-0.5 flex flex-wrap items-center gap-2">
        {binding.role !== 'unclassified' && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${ROLE_BADGE[binding.role]}`}
          >
            {binding.role}
          </span>
        )}
        {binding.required_for_completion && (
          <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800">
            Required for completion
          </span>
        )}
        {taskId && !withdrawn && binding.role === 'unclassified' && (
          <ClassifyControl
            binding={binding}
            busy={busy}
            onClassify={(role, required) =>
              mutate({ action: 'classify', role, required_for_completion: required })
            }
          />
        )}
        {taskId && !withdrawn && !withdrawOpen && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setWithdrawOpen(true)}
            className="text-[11px] font-medium text-gray-500 hover:text-red-700 disabled:opacity-50"
          >
            Withdraw
          </button>
        )}
        {taskId && withdrawn && (
          <button
            type="button"
            disabled={busy}
            onClick={() => mutate({ action: 'reactivate' })}
            className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Reattach
          </button>
        )}
      </div>

      {withdrawn && (
        <p className="mt-0.5 text-xs text-gray-500">
          Withdrawn{binding.withdrawal_reason ? `: ${binding.withdrawal_reason}` : ''}
        </p>
      )}

      {withdrawOpen && !withdrawn && (
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
          <label htmlFor={reasonId} className="text-gray-600">
            Reason
          </label>
          <input
            id={reasonId}
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded border border-gray-300 px-1.5 py-0.5 text-xs"
          />
          <button
            type="button"
            disabled={busy || reason.trim() === ''}
            onClick={() => mutate({ action: 'withdraw', reason: reason.trim() })}
            className="rounded bg-red-700 px-1.5 py-0.5 text-[10px] font-medium text-white disabled:opacity-50"
          >
            Confirm withdrawal
          </button>
          <button
            type="button"
            onClick={() => setWithdrawOpen(false)}
            className="text-[11px] text-gray-500 hover:underline"
          >
            Cancel
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <ul className="mt-1 space-y-1 border-l border-gray-200 pl-3">
          {rows.map((artifact) => (
            <EvidenceRow key={artifact.id} artifact={artifact} muted={withdrawn} taskId={binding.task_id} />
          ))}
        </ul>
      )}
      </ArtifactIdentity>
    </li>
  );
}

export function TaskArtifactList({
  artifacts,
  bindings = [],
  taskId,
  onMutated,
}: {
  artifacts: TaskArtifact[];
  bindings?: TaskArtifactBinding[];
  taskId?: string;
  onMutated?: () => void;
}) {
  if (artifacts.length === 0 && bindings.length === 0) {
    return <p className="text-xs text-gray-500">No artifacts attached</p>;
  }

  const boundKeys = new Set(bindings.map(identityKey));
  const orphans = artifacts.filter((a) => !boundKeys.has(identityKey(a)));

  return (
    <div className="space-y-2">
      {bindings.length > 0 && (
        <ul aria-label="Task artifacts" className="space-y-2">
          {bindings.map((binding) => (
            <BindingGroup
              key={binding.id}
              binding={binding}
              rows={artifacts.filter((a) => identityKey(a) === identityKey(binding))}
              taskId={taskId}
              onMutated={onMutated}
            />
          ))}
        </ul>
      )}
      {orphans.length > 0 && (
        // Defensive: the composite FK should make a binding-less pointer impossible, but an
        // audit row is never hidden because the parent record is unexpectedly absent.
        <section aria-label="Ungrouped evidence">
          <h5 className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Ungrouped evidence
          </h5>
          <ul className="mt-1 space-y-1.5">
            {orphans.map((artifact) => (
              <EvidenceRow key={artifact.id} artifact={artifact} showLabel taskId={taskId ?? artifact.task_id} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default TaskArtifactList;
